import { useState, useMemo } from 'react';

export const useColumnFilters = (data = []) => {
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState(null);

  const handleFilterChange = (column, filter) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      if (filter) {
        newFilters[column] = filter;
      } else {
        delete newFilters[column];
      }
      return newFilters;
    });
  };

  const handleSort = (sortConfig) => {
    setSortConfig(sortConfig);
  };

  const clearAllFilters = () => {
    setFilters({});
    setSortConfig(null);
  };

  const filteredAndSortedData = useMemo(() => {
    let result = [...data];

    const normalizeRules = (filter) => {
      if (!filter) return [];
      if (Array.isArray(filter.rules) && filter.rules.length > 0) return filter.rules;
      return [filter];
    };

    const matchesRule = (item, column, rule, fallbackDataType) => {
      const dataType = rule.dataType || fallbackDataType;
      let value = item[column];

      // Special handling for reflecting_month - use formatted display text for filtering
      if (column === 'reflecting_month' && dataType === 'text') {
        if (!value) return false;
        const d = new Date(value);
        if (isNaN(d)) return false;
        value = d.toLocaleString("default", { month: "long", year: "numeric" });
      }

      if (value == null || value === '') return false;

      switch (dataType) {
        case 'text': {
          const textValue = String(value).toLowerCase();
          const providedValues = Array.isArray(rule.values) ? rule.values.filter(v => v != null && v !== '') : [];
          const normalizedValues = providedValues.map(v => String(v).toLowerCase());
          const filterValue = rule.value != null ? String(rule.value).toLowerCase() : '';

          switch (rule.operator) {
            case 'contains':
              if (normalizedValues.length) return normalizedValues.some(v => textValue.includes(v));
              return filterValue ? textValue.includes(filterValue) : false;
            case 'equals':
              if (normalizedValues.length) return normalizedValues.includes(textValue);
              return filterValue ? textValue === filterValue : false;
            case 'starts_with':
              if (normalizedValues.length) return normalizedValues.some(v => textValue.startsWith(v));
              return filterValue ? textValue.startsWith(filterValue) : false;
            case 'ends_with':
              if (normalizedValues.length) return normalizedValues.some(v => textValue.endsWith(v));
              return filterValue ? textValue.endsWith(filterValue) : false;
            case 'not_contains':
              if (normalizedValues.length) return normalizedValues.every(v => !textValue.includes(v));
              return filterValue ? !textValue.includes(filterValue) : false;
            default:
              return true;
          }
        }

        case 'number': {
          const numValue = parseFloat(value);
          const filterNum = parseFloat(rule.value);

          if (isNaN(numValue)) return false;

          switch (rule.operator) {
            case 'equals':
              if (isNaN(filterNum)) return false;
              return Math.abs(numValue - filterNum) < 0.01;
            case 'greater_than':
              if (isNaN(filterNum)) return false;
              return numValue > filterNum;
            case 'less_than':
              if (isNaN(filterNum)) return false;
              return numValue < filterNum;
            case 'between': {
              const min = parseFloat(rule.min || 0);
              const max = parseFloat(rule.max || Infinity);
              if (isNaN(min) || isNaN(max)) return false;
              return numValue >= min && numValue <= max;
            }
            default:
              return true;
          }
        }

        case 'number_range': {
          const numRangeValue = parseFloat(value);
          if (isNaN(numRangeValue)) return false;

          switch (rule.operator) {
            case 'equals': {
              const filterNumRange = parseFloat(rule.value);
              if (isNaN(filterNumRange)) return false;
              return Math.abs(numRangeValue - filterNumRange) < 0.01;
            }
            case 'greater_than': {
              const filterGt = parseFloat(rule.value);
              if (isNaN(filterGt)) return false;
              return numRangeValue > filterGt;
            }
            case 'less_than': {
              const filterLt = parseFloat(rule.value);
              if (isNaN(filterLt)) return false;
              return numRangeValue < filterLt;
            }
            case 'between': {
              const minRange = parseFloat(rule.minValue);
              const maxRange = parseFloat(rule.maxValue);
              if (isNaN(minRange) || isNaN(maxRange)) return false;
              return numRangeValue >= minRange && numRangeValue <= maxRange;
            }
            default:
              return true;
          }
        }

        case 'boolean':
          if (column === 'request_inward') {
            return Boolean(value) === rule.booleanValue;
          } else if (column === 'invoice_file') {
            const hasFile = value && value !== '' && value !== '-' && value !== null && value !== undefined;
            return hasFile === rule.booleanValue;
          }
          return Boolean(value) === rule.booleanValue;

        case 'date': {
          let itemDate;
          if (value instanceof Date) {
            itemDate = value;
          } else if (typeof value === 'string') {
            itemDate = new Date(value + 'T00:00:00.000Z');
          } else {
            return false;
          }

          const filterDate = new Date(rule.dateFrom + 'T00:00:00.000Z');

          if (isNaN(itemDate.getTime()) || isNaN(filterDate.getTime())) return false;

          const itemDateOnly = new Date(itemDate.getFullYear(), itemDate.getMonth(), itemDate.getDate());
          const filterDateOnly = new Date(filterDate.getFullYear(), filterDate.getMonth(), filterDate.getDate());

          switch (rule.operator) {
            case 'equals':
              return itemDateOnly.getTime() === filterDateOnly.getTime();
            case 'before':
              return itemDateOnly < filterDateOnly;
            case 'after':
              return itemDateOnly > filterDateOnly;
            case 'between': {
              const fromDate = new Date(rule.dateFrom + 'T00:00:00.000Z');
              const toDate = new Date(rule.dateTo + 'T00:00:00.000Z');
              if (isNaN(toDate.getTime())) return false;

              const fromDateOnly = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
              const toDateOnly = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate());

              return itemDateOnly >= fromDateOnly && itemDateOnly <= toDateOnly;
            }
            case 'contains':
              return false;
            default:
              return false;
          }
        }

        default:
          return true;
      }
    };

    // Apply filters
    Object.entries(filters).forEach(([column, filter]) => {
      const rules = normalizeRules(filter);
      result = result.filter(item => {
        return rules.every(rule => matchesRule(item, column, rule, filter.dataType));
      });
    });

    // Apply sorting
    if (sortConfig) {
      result.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        
        // Handle null/undefined values
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;
        
        // Handle different data types
        let comparison = 0;
        
        // Try to parse as numbers first
        const aNum = parseFloat(aValue);
        const bNum = parseFloat(bValue);
        
        if (!isNaN(aNum) && !isNaN(bNum)) {
          comparison = aNum - bNum;
        } else {
          // String comparison
          comparison = String(aValue).localeCompare(String(bValue));
        }
        
        return sortConfig.direction === 'desc' ? -comparison : comparison;
      });
    }

    return result;
  }, [data, filters, sortConfig]);

  const getActiveFiltersCount = () => {
    return Object.keys(filters).length;
  };

  const convertToApiParams = () => {
    const params = {};
    
    Object.entries(filters).forEach(([column, filter]) => {
      const rules = Array.isArray(filter.rules) && filter.rules.length > 0 ? filter.rules : [filter];
      rules.forEach(rule => {
        const dataType = rule.dataType || filter.dataType;
        switch (dataType) {
          case 'text':
            if (rule.operator === 'contains' || rule.operator === 'not_contains') {
              if (!params.q) params.q = [];
              if (rule.value) params.q.push(rule.value);
              if (Array.isArray(rule.values)) params.q.push(...rule.values);
            } else {
              params[column] = Array.isArray(rule.values) && rule.values.length > 0 ? rule.values.join(',') : rule.value;
            }
            break;

          case 'number':
            if (rule.operator === 'between') {
              params[`min_${column}`] = rule.min;
              params[`max_${column}`] = rule.max;
            } else {
              params[column] = rule.value;
            }
            break;

          case 'date':
            if (rule.operator === 'between') {
              params[`${column}_from`] = rule.dateFrom;
              params[`${column}_to`] = rule.dateTo;
            } else {
              params[column] = rule.dateFrom;
            }
            break;
        }
      });
    });
    
    // Convert q array to string
    if (params.q && Array.isArray(params.q)) {
      params.q = params.q.join(' ');
    }
    
    return params;
  };

  return {
    filters,
    setFilters,
    sortConfig,
    filteredAndSortedData,
    handleFilterChange,
    handleSort,
    clearAllFilters,
    getActiveFiltersCount,
    convertToApiParams
  };
};
